import { CommunicationCategory, TimelineFilterType } from '@bariendo/lib/constants';
import { getReferenceString } from '@medplum/core';
import { OperationOutcome, Organization, Practitioner, Reference } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { PARTNER_TIMELINE_QUERY, PARTNER_TIMELINE_RECIPIENT_QUERY, TimelineCommunicationResource } from '../graphql';
import { filterCommunications } from '@/services/Timeline/timelineFilter';
import { mapCommunications } from '@/services/Timeline/timelineGroups';
import { TimelinePatientCommunication, TimelineNote } from '@/components/Timeline/types';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { FilterType } from '@/components/Timeline/TimelineFilters';

/*
TODO: refact this code to unify in a single hook and use the same logic for patient and partner timelines.
*/

// Validate filter parameter from URL - Calls, SMS, and Standalone Notes
const validateFilter = (filter: string | null): FilterType => {
  const validFilters: FilterType[] = [
    TimelineFilterType.ALL,
    CommunicationCategory.SMS_MESSAGE,
    CommunicationCategory.PHONE_CALL,
    CommunicationCategory.STANDALONE_NOTE,
    CommunicationCategory.INBOUND,
    CommunicationCategory.FAX_MESSAGE,
  ];
  return validFilters.includes(filter as FilterType) ? (filter as FilterType) : TimelineFilterType.ALL;
};

const parseDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const date = dayjs(dateString);
  return date.isValid() ? date.toDate() : null;
};

const parseProviderReference = (providerString: string | null): Reference<Practitioner> | null => {
  if (!providerString || !providerString.startsWith('Practitioner/')) {
    return null;
  }
  return { reference: providerString };
};

export interface UsePartnerTimelineResult {
  activeFilter: FilterType;
  searchTerm: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedProvider: Reference<Practitioner> | null;
  timelineItems: TimelinePatientCommunication[];
  filteredTimelineItems: TimelinePatientCommunication[];
  filteredTimelineNotes: TimelineNote[];
  loading: boolean;
  hasLoadedInitialData: boolean;
  error: OperationOutcome | Error | undefined;
  pauseLiveUpdates: () => void;
  resumeLiveUpdates: () => void;
  refresh: () => void;
}

interface UsePartnerTimelineProps {
  organization: Organization | undefined;
  urlSearchParams: URLSearchParams;
}

export function usePartnerTimeline(props: UsePartnerTimelineProps): UsePartnerTimelineResult {
  const { organization, urlSearchParams: searchParams } = props;

  const [areLiveUpdatesPaused, setAreLiveUpdatesPaused] = useState(false);

  const { activeFilter, searchTerm, startDate, endDate, selectedProvider } = useMemo(
    () => ({
      activeFilter: validateFilter(searchParams.get('type')),
      searchTerm: searchParams.get('search') || '',
      startDate: parseDate(searchParams.get('startDate')),
      endDate: parseDate(searchParams.get('endDate')),
      selectedProvider: parseProviderReference(searchParams.get('provider')),
    }),
    [searchParams]
  );
  const firstLoadTracker = useRef<[TimelinePatientCommunication[], TimelineNote[]]>(undefined);

  const organizationRef = organization ? getReferenceString(organization) : undefined;

  const queryVariables = useMemo(() => {
    if (!organizationRef) return {};

    let categories: string[];

    if (activeFilter === TimelineFilterType.ALL) {
      categories = [
        CommunicationCategory.PHONE_CALL,
        CommunicationCategory.SMS_MESSAGE,
        CommunicationCategory.STANDALONE_NOTE,
        CommunicationCategory.INBOUND,
        CommunicationCategory.FAX_MESSAGE,
      ];
    } else {
      categories = [activeFilter];
    }

    firstLoadTracker.current = undefined;

    return {
      categories: categories.join(','),
      organizationRef: organizationRef,
    };
  }, [organizationRef, activeFilter]);

  // Query for Communications where organization is sender
  const {
    data: senderData,
    loading: senderLoading,
    hasLoadedInitialData: senderHasLoaded,
    error: senderError,
    refresh: refreshSenderTimeline,
  } = useLiveGraphQL<TimelineCommunicationResource>(PARTNER_TIMELINE_QUERY, queryVariables, {
    pageSize: 100,
    pauseLiveUpdates: areLiveUpdatesPaused,
    liveUpdateDelay: 1,
  });

  // Query for Communications where organization is recipient
  const {
    data: recipientData,
    loading: recipientLoading,
    hasLoadedInitialData: recipientHasLoaded,
    error: recipientError,
    refresh: refreshRecipientTimeline,
  } = useLiveGraphQL<TimelineCommunicationResource>(PARTNER_TIMELINE_RECIPIENT_QUERY, queryVariables, {
    pageSize: 100,
    pauseLiveUpdates: areLiveUpdatesPaused,
    liveUpdateDelay: 1,
  });

  // Combine data from both queries and deduplicate by ID
  const data = useMemo(() => {
    const combined = [...senderData, ...recipientData];
    const uniqueMap = new Map<string, TimelineCommunicationResource>();
    combined.forEach((item) => {
      if (item.id) {
        uniqueMap.set(item.id, item);
      }
    });
    return Array.from(uniqueMap.values());
  }, [senderData, recipientData]);

  const loading = senderLoading || recipientLoading;
  const hasLoadedInitialData = senderHasLoaded && recipientHasLoaded;
  const error = senderError || recipientError;

  const refreshTimeline = useCallback(() => {
    refreshSenderTimeline();
    refreshRecipientTimeline();
  }, [refreshSenderTimeline, refreshRecipientTimeline]);

  const [allTimelineRegisters, allTimelineNotes] = useMemo(() => {
    if (data.length === 0) {
      return [[], []];
    }

    // No need for client-side filtering anymore since we're filtering server-side
    // Data is already filtered by sender or recipient in the GraphQL queries
    if (firstLoadTracker.current === undefined && data.length > 0) {
      // Do an initial load with just the items in the first page of results
      const firstItems = mapCommunications(data);
      firstLoadTracker.current = firstItems;
      return firstItems;
    }

    if (loading) {
      return firstLoadTracker.current ?? [[], []];
    }

    return mapCommunications(data);
  }, [data, loading]);

  const [filteredTimelineRegisters, filteredTimelineNotes] = useMemo(() => {
    const [filteredRegisters, filteredNotes] = filterCommunications(
      allTimelineRegisters,
      allTimelineNotes,
      searchTerm,
      selectedProvider,
      startDate,
      endDate
    );

    return [filteredRegisters, filteredNotes];
  }, [allTimelineRegisters, allTimelineNotes, searchTerm, selectedProvider, startDate, endDate]);

  const pauseLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(true);
  }, []);

  const resumeLiveUpdates = useCallback(() => {
    setAreLiveUpdatesPaused(false);
  }, []);

  const refresh = useCallback(() => {
    setAreLiveUpdatesPaused(false);
    refreshTimeline();
  }, [refreshTimeline]);

  return {
    activeFilter,
    searchTerm,
    startDate,
    endDate,
    selectedProvider,
    timelineItems: allTimelineRegisters,
    filteredTimelineItems: filteredTimelineRegisters,
    filteredTimelineNotes: filteredTimelineNotes,
    loading,
    hasLoadedInitialData,
    error,
    pauseLiveUpdates,
    resumeLiveUpdates,
    refresh,
  };
}
